import React from 'react';
import './Terms.css';
import Header from './Header';
import Footer from './Footer';

const Terms = () => {
  return (
    <div className="terms-page">
      <Header />
      <section className="terms-section">
        <div className="container">
          <h1 className="section-title terms-title">Terms of Service</h1>
          
          <div className="terms-content">
            <p className="terms-updated">Last updated: December 2025</p>

            <h2 className="terms-heading">1. Acceptance of Terms</h2>
            <p className="terms-text">
              By accessing or using Nanocaps ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, then you may not access the Service.
            </p>
            <p className="terms-text">
              These Terms apply to all visitors, users, and others who access or use the Service.
            </p>

            <h2 className="terms-heading">2. Description of Service</h2>
            <p className="terms-text">
              Nanocaps is an AI-powered system intelligence platform that helps organizations understand and map their technical infrastructure, workflows, and integration dependencies. Our platform ingests documents, conducts AI-led stakeholder conversations, and generates comprehensive system maps to support digital transformation and integration projects.
            </p>
            <p className="terms-text">
              The Service includes document ingestion, stakeholder interviews, system mapping, workflow visualization, and integration dependency analysis.
            </p>

            <h2 className="terms-heading">3. Eligibility</h2>
            <p className="terms-text">
              You must be at least 18 years old to use the Service. By using the Service, you represent and warrant that you are at least 18 years of age and have the legal capacity to enter into these Terms.
            </p>

            <h2 className="terms-heading">4. User Accounts and Access</h2>
            <p className="terms-text">
              To access the Service, you are required to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
            </p>
            <p className="terms-text">
              You agree to provide accurate, current, and complete information during registration and to update such information to keep it accurate, current, and complete. You must notify us immediately of any unauthorized use of your account.
            </p>

            <h2 className="terms-heading">5. Acceptable Use</h2>
            <p className="terms-text">You agree not to:</p>
            <ul className="terms-list">
              <li>Use the Service for any illegal purpose or in violation of any laws or regulations</li>
              <li>Attempt to gain unauthorized access to the Service or its related systems or networks</li>
              <li>Interfere with or disrupt the Service or servers or networks connected to the Service</li>
              <li>Use any automated system, including "robots," "spiders," or "offline readers," to access the Service</li>
              <li>Reproduce, duplicate, copy, sell, resell, or exploit any portion of the Service without express written permission</li>
              <li>Use the Service to transmit any viruses, worms, or other malicious code</li>
              <li>Impersonate any person or entity or misrepresent your affiliation with any person or entity</li>
            </ul>

            <h2 className="terms-heading">6. Intellectual Property</h2>
            <p className="terms-text">
              The Service and its original content, features, and functionality are owned by Currahee Limited and are protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>
            <p className="terms-text">
              You may not modify, reproduce, distribute, create derivative works, publicly display, or publicly perform any content from the Service without our prior written permission.
            </p>
            <p className="terms-text">
              The Nanocaps name, logo, and all related names, logos, product and service names, designs, and slogans are trademarks of Currahee Limited or its affiliates.
            </p>

            <h2 className="terms-heading">7. User Content and Data</h2>
            <p className="terms-text">
              When you use the Service, you may provide content such as system documentation, architecture diagrams, stakeholder interviews, and other data ("User Content"). You retain ownership of your User Content, but you grant us a license to use, process, and analyze it to provide and improve the Service.
            </p>
            <p className="terms-text">We use your data to:</p>
            <ul className="terms-list">
              <li>Generate system maps and integration visualizations</li>
              <li>Provide insights and recommendations for digital transformation</li>
              <li>Improve our AI models and the overall Service</li>
            </ul>
            <p className="terms-text">
              You are responsible for the content you provide and must not submit any content that is illegal, offensive, or violates third-party rights.
            </p>

            <h2 className="terms-heading">8. Service Disclaimer</h2>
            <p className="terms-text">
              <strong>Important:</strong> Nanocaps is a system intelligence tool designed to help organizations understand their technical infrastructure and plan transformations. While we strive to provide accurate analysis and helpful insights:
            </p>
            <ul className="terms-list">
              <li>We do not guarantee the completeness or accuracy of all system mappings</li>
              <li>AI-generated insights may not capture all technical nuances or dependencies</li>
              <li>System maps should be validated by your technical teams before making critical decisions</li>
              <li>You should use the Service as one component of your overall transformation planning</li>
            </ul>
            <p className="terms-text">
              The Service is provided for analysis and planning purposes only. Your success in digital transformation depends on many factors beyond our control.
            </p>

            <h2 className="terms-heading">9. Limitation of Liability</h2>
            <p className="terms-text">
              To the maximum extent permitted by law, Currahee Limited and its officers, directors, employees, and agents shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
            </p>
            <ul className="terms-list">
              <li>Your use or inability to use the Service</li>
              <li>Any errors or omissions in the Service's content</li>
              <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
              <li>Any interruption or cessation of transmission to or from the Service</li>
              <li>Any outcomes of integration or transformation projects</li>
              <li>Any decisions made based on insights provided by the Service</li>
            </ul>
            <p className="terms-text">
              Our total liability to you for all claims arising from or related to the Service shall not exceed the amount you paid to us in the 12 months preceding the claim.
            </p>

            <h2 className="terms-heading">10. Indemnification</h2>
            <p className="terms-text">
              You agree to defend, indemnify, and hold harmless Currahee Limited and its officers, directors, employees, and agents from and against any claims, liabilities, damages, losses, and expenses, including reasonable legal fees, arising out of or in any way connected with your use of the Service or violation of these Terms.
            </p>

            <h2 className="terms-heading">11. Subscription and Payment</h2>
            <p className="terms-text">
              Certain features of the Service may require payment of fees. By subscribing to a paid plan, you agree to pay all applicable fees and charges as described at the time of purchase.
            </p>
            <p className="terms-text">
              Subscription fees are billed in advance and are non-refundable except as required by law. You may cancel your subscription at any time, but no refunds will be provided for partial periods.
            </p>
            <p className="terms-text">
              We reserve the right to change our pricing at any time. Price changes will not affect your current billing cycle and will take effect at the start of your next billing period.
            </p>

            <h2 className="terms-heading">12. Termination</h2>
            <p className="terms-text">
              We may terminate or suspend your access to the Service immediately, without prior notice or liability, for any reason, including if you breach these Terms or engage in prohibited conduct.
            </p>
            <p className="terms-text">
              You may terminate your account at any time by contacting us or through your account settings. Upon termination, your right to use the Service will cease immediately.
            </p>
            <p className="terms-text">
              All provisions of these Terms that by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>

            <h2 className="terms-heading">13. Changes to Terms</h2>
            <p className="terms-text">
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
            </p>
            <p className="terms-text">
              What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms.
            </p>

            <h2 className="terms-heading">14. Governing Law</h2>
            <p className="terms-text">
              These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to its conflict of law provisions.
            </p>
            <p className="terms-text">
              Any disputes arising out of or relating to these Terms or the Service shall be subject to the exclusive jurisdiction of the courts of England and Wales.
            </p>

            <h2 className="terms-heading">15. Severability</h2>
            <p className="terms-text">
              If any provision of these Terms is held to be invalid or unenforceable by a court, the remaining provisions of these Terms will remain in effect. The invalid or unenforceable provision will be replaced with a valid provision that most closely matches the intent of the original provision.
            </p>

            <h2 className="terms-heading">16. Entire Agreement</h2>
            <p className="terms-text">
              These Terms, together with our Privacy Policy, constitute the entire agreement between you and Currahee Limited regarding the Service and supersede all prior agreements and understandings.
            </p>

            <h2 className="terms-heading">17. Contact Information</h2>
            <p className="terms-text">
              If you have any questions about these Terms of Service, please contact us:
            </p>
            <p className="terms-text">
              <strong>Currahee Limited</strong><br />
              86-90 Paul Street<br />
              London, England<br />
              United Kingdom, EC2A 4NE<br />
              <br />
              Email: pierre@nanocaps.ai
            </p>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default Terms;

